import React from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Pickaxe, Trees } from 'lucide-react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import SiteContextLine from 'src/app/site-nature-risk/components/site-context-line/SiteContextLine';
import SiteInactiveNotice from 'src/app/site-nature-risk/components/site-inactive-notice/SiteInactiveNotice';
import Show from 'src/shared/components/show/Show';
import GlobalNatureRiskGate from 'src/app/portfolio/views/global-nature-risk/GlobalNatureRiskGate';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import RiskScoreCard from 'src/shared/components/risk-score-card/RiskScoreCard';
import NatureStatusCard from 'src/shared/components/nature-status-card/NatureStatusCard';
import ReportingFrameworks from 'src/shared/components/reporting-frameworks/ReportingFrameworks';
import { useGetSiteNatureRiskOverviewQuery } from 'src/app/site-nature-risk/state/api/siteNatureRiskEngineSlice';
import { SiteStatus } from 'src/app/site-nature-risk/state/api/siteStatus';
import TimePeriodFilter from 'src/shared/components/filters/time-period-filter/TimePeriodFilter';
import useSiteAvailableTimePeriods from 'src/app/site-nature-risk/hooks/useSiteAvailableTimePeriods';
import useSelectedTimePeriod from 'src/shared/hooks/useSelectedTimePeriod';
import MethodologySheet from 'src/app/portfolio/views/global-nature-risk/overview/components/MethodologySheet';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { SiteNatureRiskViews } from 'src/routes/routesConfig';
import { usePortfolioSitesForQuadrant } from './components/usePortfolioSitesForQuadrant';
import RiskDynamicsBreakdownCard from './components/RiskDynamicsBreakdownCard';

const SiteNatureRiskOverviewContent = () => {
    const [isMethodologyOpen, setIsMethodologyOpen] = React.useState(false);
    const { siteId } = useParams<{ siteId: string }>();
    const { currentCustomerId } = useCustomer();
    const navigation = useAppNavigation();

    const { timePeriods } = useSiteAvailableTimePeriods(currentCustomerId, siteId);
    const { selectedAt, setSelectedAt } = useSelectedTimePeriod();

    const overviewQuery = useGetSiteNatureRiskOverviewQuery(
        { organisationBusinessCentralId: currentCustomerId, siteId: siteId!, at: selectedAt ?? undefined },
        { skip: !currentCustomerId || !siteId }
    );
    const { data, isLoading, currentData, isFetching } = overviewQuery;

    const {
        sites: portfolioSites,
        truncated: portfolioTruncated,
        siteLimit,
        isLoading: portfolioLoading,
        totalCount: portfolioTotalCount,
    } = usePortfolioSitesForQuadrant(currentCustomerId, selectedAt ?? undefined);

    const site = data?.site;
    const riskSummary = site?.riskSummary ?? null;
    const portfolioRank = site?.portfolioRank ?? null;
    const isInactive = site !== undefined && site !== null && site.status !== SiteStatus.ACTIVE;

    const breadcrumbs = [{ label: 'Nature Risk', path: navigation.globalNatureRiskOverviewPath }, { label: site?.name ?? '' }];

    const impactsAndDependenciesPath = navRoutesConfig[SiteNatureRiskViews.IMPACTS_AND_DEPENDENCIES]({
        siteId: siteId ?? '',
        customerId: currentCustomerId ?? '',
    });
    const stateOfNaturePath = navRoutesConfig[SiteNatureRiskViews.STATE_OF_NATURE]({
        siteId: siteId ?? '',
        customerId: currentCustomerId ?? '',
    });

    return (
        <>
            <StandardAppLayout
                title={site?.name ?? ''}
                subTitle='Site overview of nature-related risks'
                stickyHeader={false}
                breadcrumbs={breadcrumbs}
                classNames={{ otherActions: '!justify-start !p-0' }}
                otherActions={<TimePeriodFilter periods={timePeriods} selectedValue={selectedAt} onSelect={setSelectedAt} />}
                mainContent={
                    <div className='flex flex-col w-full gap-6 flex-1 min-h-0 mb-6' data-testid='site-nature-risk-overview-main'>
                        <SiteContextLine
                            countryName={site?.geography?.countryName ?? null}
                            activity={site?.activity}
                            status={site?.status}
                            archivedAt={site?.validTo ?? null}
                        />

                        <Show when={!isInactive} fallback={<SiteInactiveNotice />}>
                            {/* Physical Nature Risk section */}
                            <div className='grid grid-cols-3 gap-4'>
                                <RiskScoreCard
                                    title='Physical Nature Risk'
                                    value={riskSummary?.physicalNatureRisk ?? null}
                                    scoreType='Risk'
                                    isLoading={isLoading}
                                    size='lg'
                                    slug='physical-nature-risk'
                                    explainerSlug='physical-nature-risk-te'
                                    portfolioRank={portfolioRank}
                                    scoreLabel='Physical Risk Score'
                                />
                                <div className='col-span-2'>
                                    <NatureStatusCard
                                        text={currentData?.site?.natureStatus ?? null}
                                        // Not `isLoading || isFetching`: engineApiSlice sets
                                        // refetchOnFocus/refetchOnReconnect, so isFetching also goes true on a
                                        // silent background revalidation of the site we're already showing -
                                        // that would flicker to the skeleton for no visible reason. Checking
                                        // !currentData limits this to switches where we genuinely don't have
                                        // a resolved result for the current site yet.
                                        isLoading={isLoading || (isFetching && !currentData)}
                                    />
                                </div>
                            </div>

                            {/* Risk Dynamics Breakdown */}
                            <RiskDynamicsBreakdownCard
                                sites={portfolioSites}
                                highlightedSiteId={site?.id}
                                truncated={portfolioTruncated}
                                siteLimit={siteLimit}
                                isLoading={portfolioLoading}
                                totalCount={portfolioTotalCount}
                            />

                            {/* Contributors section */}
                            <div className='flex flex-col gap-4'>
                                <div className='flex items-center justify-between'>
                                    <div className='flex items-center gap-3'>
                                        <span className='text-[20px] font-normal text-[#212121]'>Physical Nature Risk contributors</span>
                                        <button
                                            type='button'
                                            data-testid='contributors-learn-more-btn'
                                            className='border border-border rounded-lg px-2 py-0.5 text-xs text-foreground'
                                            onClick={() => setIsMethodologyOpen(true)}
                                        >
                                            Learn more
                                        </button>
                                    </div>
                                    <ReportingFrameworks slugPrefix='gnr' />
                                </div>
                                <div className='grid grid-cols-3 gap-4'>
                                    <RiskScoreCard
                                        title='Dependencies'
                                        value={riskSummary?.operationalDependencies ?? null}
                                        scoreType='Risk'
                                        isLoading={isLoading}
                                        size='md'
                                        icon={Building2}
                                        slug='dependencies'
                                        explainerSlug='dependencies-te'
                                        exploreTo={impactsAndDependenciesPath}
                                    />
                                    <RiskScoreCard
                                        title='Impact on Nature'
                                        value={riskSummary?.impactsOnNature ?? null}
                                        scoreType='Risk'
                                        isLoading={isLoading}
                                        size='md'
                                        icon={Pickaxe}
                                        slug='impact-on-nature'
                                        explainerSlug='impact-on-nature-te'
                                        exploreTo={impactsAndDependenciesPath}
                                    />
                                    <RiskScoreCard
                                        title='State of Nature'
                                        value={riskSummary?.stateOfNature ?? null}
                                        isLoading={isLoading}
                                        size='md'
                                        icon={Trees}
                                        slug='state-of-nature'
                                        explainerSlug='state-of-nature-te'
                                        scoreType='Condition'
                                        exploreTo={stateOfNaturePath}
                                    />
                                </div>
                            </div>
                        </Show>
                    </div>
                }
            />
            <MethodologySheet
                open={isMethodologyOpen}
                onOpenChange={setIsMethodologyOpen}
                onStateOfNature={siteId ? () => navigation.toSiteNatureRiskStateOfNature(siteId) : undefined}
            />
        </>
    );
};

/** SiteNatureRiskOverview, shown only to a user who may see Global Nature Risk. */
const SiteNatureRiskOverview = () => (
    <GlobalNatureRiskGate>
        <SiteNatureRiskOverviewContent />
    </GlobalNatureRiskGate>
);

export default SiteNatureRiskOverview;
